import React, { useState, useEffect  } from 'react';
import { useNavigate } from 'react-router-dom';
import sectionApi,{SectionPostParams} from '../../services/sectionApi';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  UniqueIdentifier,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {  message, Modal } from 'antd';
import OrganiseMobilePreviewPage  from "./OrganiseMobilePreview";
import Spinner from '../../components/custom/Spinner';
import NewSectionPopup from './NewSectionPopup';
import PageLoader from "../../components/custom/PageLoader";

type Item = {
  id: string;
  sectionName: string;
  sectionOrder: number | null;
  isDefault: boolean;
  createdAt: Date;
};

interface SortableItemProps {
  item: Item;
  onRemove: (id: string) => void;
  onEdit : (item: Item) => void;
}

function SortableItem({ item, onRemove, onEdit }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: item.id });

  const style: React.CSSProperties = {
    ...styles.listItem,
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
   
  <div ref={setNodeRef} style={style}>
  {/* Draggable area: drag handle + title */}
  <div style={{ display: 'flex', alignItems: 'center', flex: 1,
    minHeight: '40px',
   }} {...attributes} {...listeners}>
    <span
      style={{
        cursor: 'grab',
        marginRight: '12px',
        fontSize: '20px',
        userSelect: 'none',
      }}
      aria-label="Drag"
      tabIndex={0}
    >
      {/* ☰ */}
    </span>
    <h3 style={{...styles.title , margin: 0, position: 'relative', minHeight: '20px'}}>{item.sectionName}
      {/* Invisible fallback if title is empty */}
          {item.sectionName.trim() === '' && (
            <span
              style={{
                visibility: 'hidden',
                position: 'absolute',
                fontSize: '1px',
                height: 0,
                overflow: 'hidden',
              }}
            >
              invisible-title
            </span>
          )}
        </h3>
  </div>
  {/* Non-draggable area: Edit/Remove buttons */}
  {!item.isDefault && (
    <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
      <button style={{...styles.whiteButton, width: 90}} onClick={() => onEdit(item)}>
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="12" style={{ marginRight: '6px' }} viewBox="0 0 21.188 20.843">
        <path id="edit-text" d="M4.709,18.56h.108L7.7,18.294a1.741,1.741,0,0,0,1.064-.5l10.9-10.9a2.689,2.689,0,0,0,0-3.824l-.7-.7a2.774,2.774,0,0,0-3.833,0l-1.39,1.39h0l-9.5,9.5a1.678,1.678,0,0,0-.493,1.064L3.487,17.21a1.266,1.266,0,0,0,.355.985,1.216,1.216,0,0,0,.867.365ZM17.057,3.049a1.193,1.193,0,0,1,.867.365l.7.7a1.216,1.216,0,0,1,.365.867,1.2,1.2,0,0,1-.365.867l-.867.867L15.322,4.28l.867-.867a1.233,1.233,0,0,1,.867-.365ZM5.221,14.46a.21.21,0,0,1,.069-.148l8.978-8.987L16.7,7.759,7.725,16.737a.412.412,0,0,1-.148.069l-2.592.237.237-2.592ZM22.438,21.7a.744.744,0,0,1-.739.739H1.989a.739.739,0,0,1,0-1.478H21.7A.744.744,0,0,1,22.438,21.7Z" transform="translate(-1.25 -1.6)" fill="#3e767a" />
      </svg>
        Edit
      </button>
      <button style={styles.whiteButton} onClick={() => onRemove(item.id)}>
            <svg id="delete_24dp_5F6368" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 27.963 27.963">
              <path id="Path_1338" data-name="Path 1338" d="M0,0H27.963V27.963H0Z" fill="none" />
              <path id="Path_1339" data-name="Path 1339" d="M16.955,9.9v11.5H8.26V9.9h8.694M15.325,3H9.891L8.8,4.15H5v2.3H20.215V4.15h-3.8Zm3.8,4.6H6.087V21.393a2.246,2.246,0,0,0,2.174,2.3h8.694a2.246,2.246,0,0,0,2.174-2.3Z" transform="translate(1.374 0.635)" fill="#3e767a" stroke="#f9f9fa" stroke-width="0.6" />
            </svg>
        Delete
      </button>
    </div>
  )}
</div>
  );
}

const OrganiseHomeScreenPage: React.FC = () => {

  const [items, setItems] = useState<Item[]>([]);
  const [originalItems, setOriginalItems] = useState<Item[]>([]);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [showMobilePreview, setShowMobilePreview] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState<boolean>(true);
  const [showNewSectionPopup, setShowNewSectionPopup] = useState<boolean>(false);
  const [countOfNonDefaultSection, setCountOfNonDefaultSection] = useState<number>(0);
  const [saveModal, setSaveModal] = useState<{ open: boolean }>({ open: false });
  const [discardModal, setDiscardModal] = useState<{ open: boolean }>({ open: false });
  const [isNewSectionSaving, setIsNewSectionSaving] = useState(false);
  const [removeSectionModal, setRemoveSectionModal] = useState<{ open: boolean; sectionId?: string }>
      ({ open: false });
  
   const navigate = useNavigate();

    useEffect(() => {
    const fetchData = async () => {
      try {
        await getSectionItems();
      } catch (err) {
        console.error("Failed to load data:", err);
      } finally {
        setIsPageLoading(false);
      }
    };
    fetchData();
  }, []);


  const getSectionItems = async () => {
    try {
      const sectionWithRawDate = await sectionApi.getSection();

      const nonDefaultCount = sectionWithRawDate.filter(item => item.isDefault === false).length;
      setCountOfNonDefaultSection(nonDefaultCount);

      const sortedItems = sectionWithRawDate.sort((a: Item, b: Item) => 
            a.sectionOrder - b.sectionOrder);
      /*
      const sectionItems: Item[] = sectionWithRawDate.map((item: any) => ({
        ...item,
        createdAt: new Date(item.createdAt),
      }));

      const sortedItems = sectionItems.sort((a: Item, b: Item) => {
      const aHasOrder = a.sectionOrder !== null && a.sectionOrder !== undefined;
      const bHasOrder = b.sectionOrder !== null && b.sectionOrder !== undefined;

        // Case 1: Both have null sectionOrder - sort by createdAt DESC
      if (!aHasOrder && !bHasOrder) {
          return b.createdAt.getTime() - a.createdAt.getTime();
       }

        // Case 2: Only one has null - null comes first
      if (!aHasOrder && bHasOrder) return -1;
      if (aHasOrder && !bHasOrder) return 1;

        // Case 3: Both have sectionOrder - sort by sectionOrder ASC
        return (a.sectionOrder ?? 0) - (b.sectionOrder ?? 0);
      });
      */
      
      setItems(sortedItems);
      setOriginalItems(sortedItems);
      
    } catch (error) {
      console.error("Error loading items:", error);
    }
  };

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event: DragEndEvent) => {

    if (!isEditMode) return;

    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((item) => item.id === active.id);
    const newIndex = items.findIndex((item) => item.id === over.id);

    setItems(arrayMove(items, oldIndex, newIndex));
  };

  const handleEdit = async () => {
    setIsEditMode(true);
  };


  const handleRemove = async () => {
    if (!removeSectionModal.sectionId) return;

    try {
      setIsPageLoading(true);
      const response = await sectionApi.deleteSection(removeSectionModal.sectionId);
      if(response.status == 200){
          setRemoveSectionModal({ open: false });
          messageApi.success(`Section deleted successfully!`);
          setIsEditMode(false);
          await getSectionItems();
      }
    } catch (error) {
      messageApi.error(`Removed failed`);
    }
    finally{
      setRemoveSectionModal({ open: false });
      setIsPageLoading(false);
    }
  };

  const handleEditSection = (item: Item) => {
    navigate('/section-subsection', 
      {
        state: {
          sectionId: item.id,
          sectionName: item.sectionName
        }
      }
    );
  };

  const handleDiscard = () => {
    setItems(originalItems);
    setIsEditMode(false);
    messageApi.success(`Changes discarded. The arrangement has been reset to the previously saved order.`);
    setDiscardModal({ open: false });
  };

  const handleNewSection = async () => {
    setShowNewSectionPopup(true);
  };

  const closeNewSectionPopup = async () => {
    setShowNewSectionPopup(false);
  };

  const handleDiscardSection = async () => {
    setShowNewSectionPopup(false);
  };

  const handleSaveSection = async (sectionTitle: string) => {
    try {
      if(countOfNonDefaultSection >= 3){
        messageApi.error('Maximum 3 sections are allowed!');
        return;
      }

      setIsNewSectionSaving(true);
      const sectionParams: SectionPostParams = {
        sectionName: sectionTitle
      };

      const response = await sectionApi.postSection(sectionParams);

      if (response.status == 200) {
        messageApi.success(`Section Created Successfully!`);
        setIsEditMode(false);
        await getSectionItems();
        setShowNewSectionPopup(false);
      }
    }
    catch (err) {
      const apiErrorMessage = err?.response?.data?.Message;
      messageApi.error(apiErrorMessage || 'Something went wrong');
      console.error("Error saving new section:", err);
    }
    finally{
      setIsNewSectionSaving(false);
    }
  };

  //Handle for Publish Button
  const handleSave = async () => {
    setIsSaving(true);
    const updatedItems = items.map((item, index) =>
    ({
      ...item,
      sectionOrder: index,
    }));

    try {
      setSaveModal({ open: false });
      const response = await sectionApi.putSection(updatedItems);
      if (response.status == 200) {
        messageApi.success(`Changes published successfully. The new arrangement is now live for end users.`);
        setIsEditMode(false);
        setOriginalItems(updatedItems);
      }
    } catch (error) {
      messageApi.error(`Changes save failed`);
    }
    finally {
      setIsSaving(false);
      setSaveModal({ open: false });
    }
  };

  return (
    <>
   {contextHolder}

    {isPageLoading && (
      <PageLoader />
    )}

      <Modal
        title="Are you sure you want to publish the changes? The updated arrangement will be visible to all end users."
        open={saveModal.open}
        onOk={() => handleSave()}
        onCancel={() => setSaveModal({ open: false })}
        okText="Yes"
        cancelText="No"
        centered
        okButtonProps={{
            style: styles.okButton,
            type: "default",
        }}
      />
      
      <Modal
        title="Are you sure you want to discard the changes? All unsaved changes will be lost."
        open={discardModal.open}
        onOk={() => handleDiscard()}
        onCancel={() => setDiscardModal({ open: false })}
        okText="Yes"
        cancelText="No"
        centered
        okButtonProps={{
            style: styles.okButton,
            type: "default",
        }}
      />

      <Modal
        title="Are you sure you want to delete the section ?"
        open={removeSectionModal.open}
        onOk={handleRemove}
        onCancel={() => setRemoveSectionModal({ open: false })}
        okText="Yes"
        cancelText="No"
        centered
        okButtonProps={{
            style: styles.okButton,
            type: "default",
        }}
      />

    <div // style={{ padding: "2rem" }} 
    className="reorganisecontainer">
        
        {/* Header section with title and buttons */}
        <div style={styles.headerTitle}>
          {/* Left: Title and Subtitle */}
        <div>
          <h1 style={{ fontSize: '28px', color: '#363636' }}>
            Rearrange Sections
          </h1>
          <p style={{ fontSize: '14px', color: '#363636', marginTop: '10px' }}>
            Simply drag the sections to arrange them in the order you prefer for a better view.
          </p>
        </div>

        {/* Right: Buttons */}
        <div style={styles.buttonGroup}>
          {!isEditMode && (
            <button onClick={handleEdit} style={{...styles.whiteButton, width: 90}}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="12" style={{ marginRight: '6px' }} viewBox="0 0 21.188 20.843">
                  <path id="edit-text" d="M4.709,18.56h.108L7.7,18.294a1.741,1.741,0,0,0,1.064-.5l10.9-10.9a2.689,2.689,0,0,0,0-3.824l-.7-.7a2.774,2.774,0,0,0-3.833,0l-1.39,1.39h0l-9.5,9.5a1.678,1.678,0,0,0-.493,1.064L3.487,17.21a1.266,1.266,0,0,0,.355.985,1.216,1.216,0,0,0,.867.365ZM17.057,3.049a1.193,1.193,0,0,1,.867.365l.7.7a1.216,1.216,0,0,1,.365.867,1.2,1.2,0,0,1-.365.867l-.867.867L15.322,4.28l.867-.867a1.233,1.233,0,0,1,.867-.365ZM5.221,14.46a.21.21,0,0,1,.069-.148l8.978-8.987L16.7,7.759,7.725,16.737a.412.412,0,0,1-.148.069l-2.592.237.237-2.592ZM22.438,21.7a.744.744,0,0,1-.739.739H1.989a.739.739,0,0,1,0-1.478H21.7A.744.744,0,0,1,22.438,21.7Z" transform="translate(-1.25 -1.6)" fill="#3e767a" />
                </svg>
              Edit
            </button>
          )}
          <button style={styles.greenButton}
          onClick={handleNewSection} >
              <svg id="_03_Login" data-name="03 Login" xmlns="http://www.w3.org/2000/svg" width="21.581" height="21" style={{ marginRight: '6px' }} viewBox="0 0 21.581 21">
                <path id="Path_2556" data-name="Path 2556" d="M232.054,21A1.04,1.04,0,0,1,231,19.975V1.025a1.054,1.054,0,0,1,2.108,0V19.975A1.04,1.04,0,0,1,232.054,21Z" transform="translate(-221.263)" fill="#fff" />
                <path id="Path_2557" data-name="Path 2557" d="M20.527,233.108H1.054a1.054,1.054,0,0,1,0-2.108H20.527a1.054,1.054,0,0,1,0,2.108Z" transform="translate(0 -221.554)" fill="#fff" />
              </svg>
            Add New</button>
        </div>
      </div>
      
      {/* Spacer before data list */}

      <div>
        {isEditMode ? (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={items.map((item) => item.id)} strategy={verticalListSortingStrategy}>
          <div style={styles.container}>
            {items.map((item) => (
              <SortableItem 
                key={item.id} 
                item={item}
                onEdit={handleEditSection}
                onRemove={(id: string) => setRemoveSectionModal({ open: true, sectionId: id })}
                />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      ) : (
          <div style={styles.container}>
            {items.map((item) => (
              <div key={item.id} style={styles.listItem}>
                <h3 style={styles.title}>{item.sectionName}</h3>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* In non-edit mode (inside OrganiseHomeScreenPage) */}

      {/* Spacer before bottom buttons */}
      <div style={{ marginTop: '40px' }} />

        <div style={styles.buttonGroupBottom}>
          <button onClick={() => setShowMobilePreview(true)} style={{...styles.greenButton, width: 170}}>
              Mobile Preview
          </button>
          {isEditMode && (
          <>
            <button 
            onClick={() => setDiscardModal({ open: true })}
            style={{...styles.whiteButton, width: 170, color: 'black' }}>
              Discard
            </button>
          </>
          )}
        {isEditMode && (
          <>
            <button 
            onClick={() => setSaveModal({ open: true })}
            style={{...styles.greenButton,
              backgroundColor: isSaving ? '#6a9b9e' : baseGreen,
                cursor: isSaving ? 'not-allowed' : 'pointer',
                opacity: isSaving ? 0.8 : 1,
            }}
            disabled={isSaving}>
                {isSaving && <Spinner />}
               {isSaving ? 'Publishing...' : 'Publish'}
            </button>
          </>
        )}
        </div>

        {/*Mobile area*/}
        {showMobilePreview && (
          <>
            {/* Overlay */}
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                zIndex: 1000,
              }}
              onClick={() => setShowMobilePreview(false)}
            />

            {/* Slide-out panel */}
            <div
              style={{
                position: "fixed",
                top: 0,
                right: 0,
                width: "420px",
                height: "100vh",
                backgroundColor: "rgba(0, 0, 0, 0.4)", 
                boxShadow: "-2px 0 8px rgba(0,0,0,0.3)",
                zIndex: 1001,
                padding: "1rem",
                display: "flex",
                flexDirection: "column",
                
                justifyContent: "center",
                alignItems: "center",
                gap: "25px",
              }}
            >
              <div 
                style={{ 
                //   flexGrow: 1, 
                //   display: "flex", 
                // justifyContent: "center", 
                // alignItems: "flex-start", 
                // paddingTop: "1rem" 
                  maxHeight: "calc(100vh - 140px)",
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}>
                <OrganiseMobilePreviewPage items={items} />
              </div>
              <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
              <button
                onClick={() => setShowMobilePreview(false)}
                style={{...styles.CloseButton, width: 150, color: 'black' }}
              >
                Close Preview
              </button>
              </div>
            </div>
          </>
        )}

        {showNewSectionPopup && (
        <NewSectionPopup
          onCloseWarning={closeNewSectionPopup}
          onDiscard={handleDiscardSection}
          onSave={handleSaveSection}
          isSectionSaving={isNewSectionSaving}
          mode="New"
          existingSectionTitle=""
         />
      )}


    </div>
    </>
  );
};

export default OrganiseHomeScreenPage;

const baseGreen = '#3E767A';
const hoverGreen = '#5B9492';

const buttonBase = {
  padding: '10px 20px',
    fontSize: '16px',
    borderRadius: '20px',
    backgroundColor: '#FFFFFF',
    border: `2px solid ${baseGreen}`,
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
    alignItems: "flex-start",
    padding: "1rem",
    width: "80%",
    minHeight: "400px",
  },
  listItem: {
    display: "flex",
    alignItems: "center",
    border: "1px solid #ccc",
    borderRadius: "8px",
    padding: "0.5rem 1rem",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    backgroundColor: "#fff",
    cursor: "grab",
    width: "100%",
    height: "70px",
  },
  thumbnail: {
    width: "50px",
    height: "50px",
    objectFit: "cover",
    borderRadius: "4px",
    marginRight: "1rem",
  },
  title: {
    color: '#3E767A',
    textAlign: 'left',
    fontSize: '25px',
    fontFamily: '"Merriweather Sans", sans-serif',
    margin: 0,
    lineHeight: 1,
    flex: 1, 
  },
  discardButton: {
    ...buttonBase,
    backgroundColor: '#6c757d',
    color: '#FFFFFF',
    width: '170px',
    height: '40px',
  },
  whiteButton :
	{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: "8px 24px",
    fontSize: '13px',
    borderRadius: '20px',
    border: `2px solid #f1f2f4`,
    color: baseGreen,
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    backgroundColor: '#f1f2f4',
    minWidth: "140px",
    boxShadow: '0 2px 0 #FAFAFA',
    transform: 'translateY(2px)',
    fontFamily: `'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif`,
  },
  greenButton :
	{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: "8px 16px",
    fontSize: '13px',
    borderRadius: '20px',
    border: `2px solid ${baseGreen}`,
    color: '#F1F2F4',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    backgroundColor: baseGreen,
    fontWeight: 600,
    fontFamily: `'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif`,
  },
  reorganisecontainer: {
    backgroundColor: 'white',
    padding: '30px',
    borderRadius: '12px',
    width: '60%',
    maxWidth: '590px',
    maxHeight: '45%',
    overflowY: 'auto',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '25px',
    textAlign: 'center',
    animation: 'fadeIn 0.3s ease-in-out',
  },
  headerTitle: {
    cursor: 'pointer',
    fontSize: '28px',
    marginBottom: '20px',
    display: 'flex', 
    justifyContent: 'space-between', 
    alignItems: 'flex-start',
  },
  subHeaderTitle:{
     cursor: 'pointer',
    fontSize: '20px',
    fontFamily: '"Merriweather Sans", sans-serif',
    letterSpacing: '0px',
    color: '#363636',
    marginBottom: '15px'
  },
  buttonGroup: {
    display: 'flex',
    gap: '20px',
    justifyContent: 'center',
  },
  buttonGroupBottom: {
    display: 'flex',
    gap: '20px',
    justifyContent: 'flex-end',
  },
  iconButton: {
    background: 'none',
    border: 'none',
    color: baseGreen,
    cursor: 'pointer',
    fontSize: '16px',
    padding: '4px 8px',
    borderRadius: '4px',
    transition: 'background 0.2s',
  },
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 9999,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
  },
  okButton: {
    backgroundColor: "#3E767A",
    borderColor: "#3E767A",
    color: "#fff",
  },
  CloseButton : {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: "8px 16px",
    fontSize: '13px',
    borderRadius: '20px',
    border: `2px solid ${baseGreen}`,
    color: '#F1F2F4',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    backgroundColor: '#f1f2f4',
    fontWeight: 600,
    fontFamily: `'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif`,
  },
};
